import React from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { IconButton, styled, TableCell, TableRow, Tooltip } from "@mui/material";
import classNames from "classnames";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useNavigate } from "hooks";

import { useIsRowExpandSelector, useReportsActions } from "../../../store";
import { ClientDealTableRow, DealTableColumn } from "../../../store/types";

import "./DealTableRow.styles.scss";

interface DealTableRowProps {
  row: ClientDealTableRow;
  columns: DealTableColumn[];
  depth?: number;
  hideTitle?: boolean;
  isRowTransparent?: boolean;
}

// e.g. 2023-06-03T00:00:00
const ISO_REGEX = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d/;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  height: 58,
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  minWidth: 120,
  height: 46
}));

export const DealTableRow: React.FC<DealTableRowProps> = React.memo(
  ({ row, columns, depth = 1, hideTitle = false, isRowTransparent = false }) => {
    const { toggleExpandRowAction } = useReportsActions();
    const expand: boolean = useIsRowExpandSelector(row.id!);
    const navigate = useNavigate();

    const goToDealReport = () => {
      if (!row.deal_id) return;
      navigate({ route: "dealReport", params: { deal_id: row.deal_id } });
    };

    const title = ISO_REGEX.test(row.title) ? Intl.DateTimeFormat().format(new Date(row.title)) : row.title;

    // const getPrefxPostx = useCallback(() => {})
    // console.log(row.postfixes, row.postfix);

    return (
      <>
        <StyledTableRow sx={isRowTransparent ? { backgroundColor: "transparent !important" } : {}}>
          {!hideTitle && (
            <TableCell
              sx={
                row.children || row.title === "Total" || row.title === "Filtered Portfolio"
                  ? { fontWeight: 600, paddingLeft: 2 * depth }
                  : { paddingLeft: 2 * depth }
              }
              className="deal-table__title-cell"
            >
              <div className="deal-table__title-cell-content">
                {row.children && (
                  <IconButton aria-label="expand row" size="sm" onClick={() => toggleExpandRowAction(row.id!)}>
                    <div className={"icon-box icon-box--" + (expand ? "open" : "close")} />
                  </IconButton>
                )}
                {Boolean(row.pme_warning) && (
                  <MaterialTooltip isShow title={row.pme_warning} centerText placement="top">
                    <InfoOutlinedIcon fontSize="sm" sx={{ fill: "#071F35", mr: "10px" }} />
                  </MaterialTooltip>
                )}
                <Tooltip
                  style={{
                    cursor: row.deal_id ? "pointer" : "default",
                    color: row.deal_id ? "#26A6AE" : "#333333"
                  }}
                  onClick={goToDealReport}
                  title={title}
                >
                  <span className="deal-table__title">{title}</span>
                </Tooltip>
              </div>
            </TableCell>
          )}
          {columns.map((column) => {
            const cellPostfix = (row.postfixes?.[column.key] || column.postfix || row.postfix || "").trim();
            return (
              <StyledTableCell
                sx={
                  row.children || row.title === "Total" || row.title === "Filtered Portfolio" ? { fontWeight: 600 } : {}
                }
                key={column.key}
                align="left"
              >
                <div
                  className={classNames("deal-table__cell-content", {
                    "deal-table__cell-content--reverse": cellPostfix === "%"
                  })}
                >
                  {cellPostfix && <span className="deal-table__postfix">{cellPostfix}</span>}
                  <span>
                    {row.valueData[column.key] === null || row.valueData[column.key] === undefined
                      ? "-"
                      : typeof row.valueData[column.key] === "number"
                        ? (row.valueData[column.key] as number).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2
                          })
                        : String(row.valueData[column.key])}
                  </span>
                </div>
              </StyledTableCell>
            );
          })}
        </StyledTableRow>

        {row.children &&
          expand &&
          row.children.map((childRow) => (
            <DealTableRow key={childRow.id} hideTitle={hideTitle} depth={depth + 1} row={childRow} columns={columns} />
          ))}
      </>
    );
  }
);
